import {
  AccountBookOutlined,
  ApartmentOutlined,
  AuditOutlined,
  BarChartOutlined,
  CarOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FundOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  ProfileOutlined,
  RollbackOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { UserMenu } from '@hq/account';
import { ApiError, type SwitchableOrgRow } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { truncateByWidth } from '@hq/utils';
import { App, Button, Drawer, Dropdown, Layout, Menu, Space, Tag, Typography, type MenuProps } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { useIsMobile } from '../components/useMobile';
import { appTitle, levelLabel } from '../config';
import { readOrgBrand, saveOrgBrandFromLogin } from '../org-brand';
import { useAuthStore } from '../stores/auth';

const { Header, Sider, Content } = Layout;

/** 侧栏菜单项：带 children 的是二级分组（分组本身不跳转）。 */
interface MenuItem {
  key: string;
  icon?: ReactNode;
  label: string;
  /** 进入该菜单所需权限码（sys_permission.perm_code）；空表示登录即可见。 */
  perm?: string;
  /** 多个权限码满足任一即可（与后端 `HasPerm(a) || HasPerm(b)` 同口径）。 */
  perms?: string[];
  children?: MenuItem[];
}

/**
 * 医院端菜单：一级 14 项（<16），按「权限码 + 功能集合」聚出二级分组。
 * 排序按使用频率与业务链路：工作台 → 申领 → 审批 → 配送签收 → 库存 → 小库 → 请购采购 → 结算 → 预算 → 退换/报修 → 基础资料 → 分析 → 系统。
 */
/** 新窗口打开的全屏大屏路由（不走账号登录态，凭屏端设备凭据独立取数）；非真实路径，不参与选中匹配。 */
const SCREEN_EXT_KEY = 'ext:/screen';

const MENU: MenuItem[] = [
  { key: '/workbench', icon: <DashboardOutlined />, label: '工作台' },
  {
    key: 'g:apply',
    icon: <FileTextOutlined />,
    label: '申领管理',
    children: [
      { key: '/apply', label: '我的申领' },
      { key: '/apply/create', label: '新建申领', perm: 'wl:apply:create' },
      { key: '/apply/favorite', label: '常购清单' },
      { key: '/shortage', label: '缺货申请' }
    ]
  },
  {
    key: 'g:approval',
    icon: <AuditOutlined />,
    label: '审批中心',
    perm: 'wl:apply:approve',
    children: [
      { key: '/approval/todo', label: '待我审批' },
      { key: '/approval/done', label: '我已审批' }
    ]
  },
  {
    key: 'g:delivery',
    icon: <CarOutlined />,
    label: '配送签收',
    perm: 'wl:delivery:sign',
    children: [
      { key: '/delivery', label: '配送签收' },
      { key: '/delivery/track', label: '配送轨迹' },
      { key: '/sign', label: '签收记录' },
      { key: '/sign/scan', label: '扫码签收' }
    ]
  },
  {
    key: 'g:stock',
    icon: <DatabaseOutlined />,
    label: '库存管理',
    perm: 'wl:inventory:manage',
    children: [
      { key: '/stock', label: '库存查询' },
      { key: '/inventory/inbound', label: '入库单' },
      { key: '/inventory/outbound', label: '出库单' },
      { key: '/stock/ledger', label: '库存流水' },
      { key: '/inventory/alert', label: '库存预警' },
      { key: '/transfer', label: '仓间调拨' },
      { key: '/stocktake', label: '库存盘点' },
      { key: '/inventory/opening', label: '期初建账' },
      { key: '/inventory/label', label: '标签打印' },
      { key: '/pda', label: 'PDA 作业台' }
    ]
  },
  {
    key: 'g:substock',
    icon: <ApartmentOutlined />,
    label: '科室小库',
    perm: 'wl:inventory:manage',
    children: [
      { key: '/substock', label: '科室小库' },
      { key: '/substock/consume', label: '消耗确认' },
      { key: '/cost', label: '成本转移' }
    ]
  },
  {
    key: 'g:purchase',
    icon: <ShoppingCartOutlined />,
    label: '请购采购',
    children: [
      { key: '/require', label: '请购单' },
      { key: '/purchase', label: '采购订单' }
    ]
  },
  {
    key: 'g:settle',
    icon: <AccountBookOutlined />,
    label: '结算对账',
    children: [
      { key: '/settle', label: '对账结算' },
      { key: '/settle/invoice', label: '发票与付款', perm: 'wl:settle:manage' }
    ]
  },
  {
    key: 'g:budget',
    icon: <FundOutlined />,
    label: '预算执行',
    children: [
      { key: '/budget', label: '预算执行' },
      { key: '/budget/plan', label: '预算编制', perm: 'wl:settle:manage' }
    ]
  },
  { key: '/return', icon: <RollbackOutlined />, label: '退换货', perm: 'wl:return:manage' },
  { key: '/workorder', icon: <ToolOutlined />, label: '报修工单' },
  {
    key: 'g:basic',
    icon: <ProfileOutlined />,
    label: '基础资料',
    children: [
      { key: '/master/material', label: '物资资料' },
      { key: '/basic/dept', label: '科室与空间' },
      { key: '/basic/warehouse', label: '仓库货位' },
      { key: '/basic/supplier', label: '供应商档案' },
      { key: '/basic/user', label: '人员与角色', perm: 'sys:user:manage' }
    ]
  },
  {
    // 驾驶舱数据来自 /api/wl/analytics|stats，后端已校验 wl:analytics:view，菜单同口径裁剪
    key: 'g:analytics',
    icon: <BarChartOutlined />,
    label: '统计分析',
    perm: 'wl:analytics:view',
    children: [
      { key: '/dashboard', label: '院级驾驶舱' },
      { key: '/reports', label: '报表中心' },
      // 大屏：全屏展示（新窗口）+ 本院列表 + 申请码确认（输入屏上 4-4-4 三段申请码）
      // 与后端 ScreenDeviceService（List/Disable 均要求 wl:analytics:view）及监管端菜单同口径
      { key: SCREEN_EXT_KEY, label: '打开大屏', perm: 'wl:analytics:view' },
      { key: '/screen-devices', label: '我的大屏', perm: 'wl:analytics:view' },
      { key: '/screen-scan', label: '申请码确认', perm: 'wl:analytics:view' }
    ]
  },
  {
    key: 'g:system',
    icon: <SettingOutlined />,
    label: '系统设置',
    children: [
      { key: '/system/flow', label: '审批流配置', perm: 'sys:approval:manage' },
      { key: '/system/budget', label: '预算与定额' },
      { key: '/system/dict', label: '数据字典' },
      { key: '/system/audit', label: '审计日志', perm: 'sys:audit:view' }
    ]
  }
];

interface PermContext {
  level?: string;
  perms?: readonly string[];
}

/** 递归按权限裁剪：分组节点子项全被裁掉时整组隐藏（fail-closed）。 */
function visibleTree(items: readonly MenuItem[], ctx: PermContext): MenuItem[] {
  return items.flatMap((item) => {
    const required = item.perms?.length ? item.perms : item.perm;
    const allowed =
      !required || ctx.level === 'admin' || hasPermission(ctx.perms, required, item.perms?.length ? 'any' : 'all');
    if (!allowed) return [];
    if (!item.children?.length) return [item];
    const children = visibleTree(item.children, ctx);
    return children.length ? [{ ...item, children }] : [];
  });
}

/** antd Menu 只认 key/icon/label/children，交出去前剥掉权限字段。 */
function toMenuItems(items: readonly MenuItem[]): MenuProps['items'] {
  return items.map(({ key, icon, label, children }) =>
    children?.length ? { key, icon, label, children: toMenuItems(children) } : { key, icon, label }
  );
}

interface LeafItem {
  key: string;
  label: string;
  /** 所属分组 key 链，用于自动展开对应的二级菜单。 */
  parents: string[];
}

/** 打平成可跳转的叶子项，并保留父级链（外部入口项不参与路径匹配/标题）。 */
function collectLeaves(items: readonly MenuItem[], parents: string[] = []): LeafItem[] {
  return items.flatMap((item) =>
    item.children?.length
      ? collectLeaves(item.children, [...parents, item.key])
      : item.key.startsWith('ext:')
        ? []
        : [{ key: item.key, label: item.label, parents }]
  );
}

/** 精确命中优先，其次取最长前缀（/apply/create 不被 /apply 抢走，/apply/:id 详情仍高亮「我的申领」）。 */
function matchLeaf(leaves: readonly LeafItem[], pathname: string): LeafItem | undefined {
  const exact = leaves.find((leaf) => leaf.key === pathname);
  if (exact) return exact;
  return leaves
    .filter((leaf) => pathname.startsWith(`${leaf.key}/`))
    .sort((a, b) => b.key.length - a.key.length)[0];
}

const RELATION_LABEL: Record<string, string> = {
  SELF: '本院',
  DUTY: '任职',
  SUPERVISED: '受监管',
  GROUP_SUB: '下属',
  ALL: '全区'
};

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const profile = useAuthStore((state) => state.profile);
  const clear = useAuthStore((state) => state.clear);
  const setSession = useAuthStore((state) => state.setSession);
  const patchProfile = useAuthStore((state) => state.patchProfile);
  // 侧栏受控折叠：底部用简化图标按钮替代默认整条 trigger
  const [collapsed, setCollapsed] = useState(false);
  // 移动端：<768px 侧栏改为抽屉，菜单选中后自动收起
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [orgs, setOrgs] = useState<SwitchableOrgRow[]>([]);
  const [switching, setSwitching] = useState(false);

  // 登录时缓存的机构名（优先简称），避免头部直接展示雪花 orgId
  const brand = readOrgBrand();
  const orgName = brand?.orgShortName || brand?.orgName || null;

  // 多机构切换（docs/05 §6-2）：主院账号可切到分院/社卫查看明细
  useEffect(() => {
    let alive = true;
    hq.auth
      .switchableOrgs()
      .then((result) => {
        if (alive) setOrgs(result.items);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [profile?.orgId]);

  const switchOrg = async (orgId: string) => {
    if (!profile || orgId === profile.orgId || switching) return;
    setSwitching(true);
    try {
      const result = await hq.auth.switchOrg(orgId);
      setSession(result.token, {
        userId: String(result.profile.userId),
        orgId: String(result.profile.orgId),
        deptId: String(result.profile.deptId),
        name: result.profile.realName ?? result.profile.userCode,
        level: result.profile.level ?? 'L3',
        perms: result.profile.perms ?? []
      });
      await saveOrgBrandFromLogin(hq, {
        orgId: String(result.profile.orgId),
        orgName: result.profile.orgName ?? null
      }).catch(() => null);
      message.success(`已切换到${result.org?.orgShortName || result.org?.orgName || '目标机构'}`);
      // 查询缓存是按机构混存的：整页刷新最稳妥，避免上一个机构的列表/详情残留
      window.location.reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.friendlyMessage : '切换机构失败');
      setSwitching(false);
    }
  };

  const switchMenu = {
    items: orgs.map((org) => ({
      key: String(org.orgId),
      label: (
        <Space size={8}>
          <span>{org.orgShortName || org.orgName}</span>
          <Tag color={org.isCurrent ? 'purple' : 'default'} style={{ marginInlineEnd: 0 }}>
            {org.isCurrent ? '当前' : RELATION_LABEL[org.relation] ?? org.relation}
          </Tag>
        </Space>
      ),
      disabled: org.isCurrent
    })),
    onClick: ({ key }: { key: string }) => void switchOrg(key)
  };

  // 菜单按后端权限码裁剪：管理员不裁剪；无权限快照的会话按 fail-closed 处理（只显示无需权限的菜单）
  const menu = useMemo(() => {
    const tree = visibleTree(MENU, { level: profile?.level, perms: profile?.perms });
    return { items: toMenuItems(tree), leaves: collectLeaves(tree) };
  }, [profile?.level, profile?.perms]);

  // 页签标题跟随路由（MENU 是页面标题的唯一来源，避免两套机制漂移）
  useEffect(() => {
    const current = matchLeaf(menu.leaves, location.pathname);
    document.title = current ? `${current.label} · ${appTitle}` : appTitle;
  }, [location.pathname, menu.leaves]);

  // 详情页高亮所属列表项：先精确匹配，再取最长前缀（/substock/consume 优先于 /substock）
  const selected = matchLeaf(menu.leaves, location.pathname)?.key ?? menu.leaves[0]?.key ?? '';

  // 二级菜单展开状态（手风琴）：同一时刻只展开一个分组
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // 路由变化（含点击二级菜单）时只保留当前页所属分组，其它已展开的分组自动收起
  useEffect(() => {
    setOpenKeys(matchLeaf(menu.leaves, location.pathname)?.parents ?? []);
  }, [location.pathname, menu.leaves]);

  // 手动点开某个分组时收起其它分组；关闭分组时保留仍然展开的那个（再次点击即全部收起）
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys((prev) => {
      const opened = keys.find((key) => !prev.includes(key));
      return opened ? [opened] : keys.slice(-1);
    });
  };

  // 移动端切换菜单后自动收起抽屉
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [selected, isMobile]);

  // 侧栏内容（logo + 菜单）：桌面放 Sider，移动端放 Drawer，两端复用同一份
  const siderContent = (
    <>
      <div className="app-logo">
        <img className="app-logo__img" src="/logo.png" alt="logo" />
        {!collapsed && <span title={appTitle}>{truncateByWidth(appTitle)}</span>}
      </div>
      <div className="app-sider__menu">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          // 折叠态由 antd 用悬浮气泡接管展开，传 openKeys 会与之打架
          {...(collapsed && !isMobile ? {} : { openKeys, onOpenChange: handleOpenChange })}
          items={menu.items}
          onClick={({ key }) =>
            key === SCREEN_EXT_KEY ? window.open('/screen', '_blank') : navigate(key)
          }
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={232}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          classNames={{ body: 'app-drawer__body' }}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider
          className="app-sider"
          theme="dark"
          width={208}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ position: 'sticky', top: 0, height: '100vh' }}
        >
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: isMobile ? 12 : 24,
            height: 56,
            // 不能用 56px 行高：antd ellipsis 单行文本是 inline-block + vertical-align: bottom，
            // 会被 56px 行盒压到最底部，姓名与「退出」就错行了；行高交回 normal 由 flex 居中
            lineHeight: 'normal',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Space>
            {/* 收起/展开：置顶栏左侧，贴近导航栏；移动端改为抽屉开关 */}
            <Button
              type="text"
              style={{ marginInlineStart: -12 }}
              icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => (isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
            />
            {!isMobile && <Typography.Text type="secondary">医院端 · 后勤物资</Typography.Text>}
            {/* 门户身份标签：与 GOV/OPS/SYSTEM 门户头部保持一致的视觉标识 */}
            <Tag color="green">HOSPITAL 门户</Tag>
          </Space>
          <Space size={isMobile ? 8 : 16} style={{ flexShrink: isMobile ? 1 : 0, minWidth: 0, whiteSpace: 'nowrap' }}>
            {orgs.length > 1 ? (
              <Dropdown menu={switchMenu} trigger={['click']} disabled={switching}>
                <Typography.Link>
                  <ApartmentOutlined />{!isMobile && ' 切换机构'}
                  {!isMobile && (
                    <Tag color="blue" style={{ marginInlineStart: 6 }}>
                      {orgs.length} 个
                    </Tag>
                  )}
                </Typography.Link>
              </Dropdown>
            ) : null}
            {profile ? (
              <UserMenu
                hq={hq}
                compact={isMobile}
                showSelfQr
                subtitle={orgName ?? undefined}
                profile={{
                  userId: profile.userId,
                  userCode: profile.userCode,
                  name: profile.name,
                  orgName: orgName,
                  deptName: profile.deptName,
                  mobile: profile.mobile,
                  email: profile.email,
                  avatarUrl: profile.avatarUrl,
                  level: profile.level,
                  levelText: levelLabel(profile.level)
                }}
                onProfileUpdated={(next) =>
                  patchProfile({
                    avatarUrl: next.avatarUrl,
                    deptName: next.deptName,
                    mobile: next.mobile,
                    email: next.email,
                    userCode: next.userCode
                  })
                }
                onLogout={() => {
                  clear();
                  navigate('/login', { replace: true });
                }}
              />
            ) : (
              <Typography.Text type="secondary">未登录</Typography.Text>
            )}
          </Space>
        </Header>
        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
